import { PageLoadingSpinner } from './components/LoadingSpinner';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import axios from 'axios';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { VendorAuthProvider } from './context/VendorAuthContext';
import Login from './pages/Login';
import './App.css';

// Lazy-loaded pages — only fetched when the user navigates to them
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const ClientApproval = lazy(() => import('./pages/ClientApproval'));
const CRM = lazy(() => import('./pages/CRM'));
const Vendors = lazy(() => import('./pages/Vendors'));
const Proposals = lazy(() => import('./pages/Proposals'));
const Invoices = lazy(() => import('./pages/Invoices'));
const StudioIdentity = lazy(() => import('./pages/StudioIdentity'));
const ProposalBuilder = lazy(() => import('./pages/ProposalBuilder'));
const ProposalPreview = lazy(() => import('./pages/ProposalPreview'));
const ProposalClientView = lazy(() => import('./pages/ProposalClientView'));
const ProposalCompare = lazy(() => import('./pages/ProposalCompare'));
const Settings = lazy(() => import('./pages/Settings'));
const VendorQuotePage = lazy(() => import('./pages/VendorQuotePage'));
const VendorProfile = lazy(() => import('./pages/VendorProfile'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const StudioPicker = lazy(() => import('./pages/StudioPicker'));
const DecisionClientPortal = lazy(() => import('./pages/DecisionClientPortal'));
const StudioDashboard = lazy(() => import('./pages/StudioDashboard'));

// Vendor Portal pages
const VendorLogin = lazy(() => import('./pages/vendor/VendorLogin'));
const VendorLayout = lazy(() => import('./pages/vendor/VendorLayout'));
const VendorDashboard = lazy(() => import('./pages/vendor/VendorDashboard'));
const VendorRFQs = lazy(() => import('./pages/vendor/VendorRFQs'));
const VendorRFQDetail = lazy(() => import('./pages/vendor/VendorRFQDetail'));
const VendorPOs = lazy(() => import('./pages/vendor/VendorPOs'));
const VendorPayments = lazy(() => import('./pages/vendor/VendorPayments'));
const VendorProfilePage = lazy(() => import('./pages/vendor/VendorProfilePage'));
const VendorSetPassword = lazy(() => import('./pages/vendor/VendorSetPassword'));

// Superadmin pages
const SuperadminLayout = lazy(() => import('./pages/superadmin/SuperadminLayout'));
const SAOverview = lazy(() => import('./pages/superadmin/SAOverview'));
const SAStudios = lazy(() => import('./pages/superadmin/SAStudios'));
const SAStudioDetail = lazy(() => import('./pages/superadmin/SAStudioDetail'));
const SAUsers = lazy(() => import('./pages/superadmin/SAUsers'));
const SABilling = lazy(() => import('./pages/superadmin/SABilling'));
const SAActivity = lazy(() => import('./pages/superadmin/SAActivity'));
const SAUsage = lazy(() => import('./pages/superadmin/SAUsage'));
const SAErrors = lazy(() => import('./pages/superadmin/SAErrors'));
const SASystem = lazy(() => import('./pages/superadmin/SASystem'));
const SATests = lazy(() => import('./pages/superadmin/SATests'));
const SAE2ETests = lazy(() => import('./pages/superadmin/SAE2ETests'));
const SAUtilities = lazy(() => import('./pages/superadmin/SAUtilities'));
const SATestDashboard = lazy(() => import('./pages/superadmin/SATestDashboard'));
const SASupport = lazy(() => import('./pages/superadmin/SASupport'));
const SASupportDetail = lazy(() => import('./pages/superadmin/SASupportDetail'));

// Support pages
const SupportPage = lazy(() => import('./pages/SupportPage'));
const SupportTicketDetail = lazy(() => import('./pages/SupportTicketDetail'));

const LazyLegalIndex = lazy(() => import('./pages/Legal'));
const LazyTermsPage = lazy(() => import('./pages/Legal').then(m => ({ default: m.TermsPage })));
const LazyPrivacyPage = lazy(() => import('./pages/Legal').then(m => ({ default: m.PrivacyPage })));
const LazyAcceptableUsePage = lazy(() => import('./pages/Legal').then(m => ({ default: m.AcceptableUsePage })));
const LazyDpaPage = lazy(() => import('./pages/Legal').then(m => ({ default: m.DpaPage })));


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.baseURL = API;

function AuthCallback() {
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];

      axios.post('/auth/session', { session_id: sessionId })
        .then(res => {
          localStorage.setItem('token', res.data.session_token);
          if (res.data.user?.studio_id) {
            localStorage.setItem('studio_id', res.data.user.studio_id);
          }
          window.location.href = '/dashboard';
        })
        .catch(() => {
          window.location.href = '/login';
        });
    }
  }, [location]);

  return <div className="flex items-center justify-center min-h-screen">Processing authentication...</div>;
}

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        await axios.get('/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location]);

  if (isAuthenticated === null) {
    return <PageLoadingSpinner />;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PageLoader() {
  return <PageLoadingSpinner />;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Login />} />
        <Route path="/select-studio" element={<StudioPicker />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/legal" element={<LazyLegalIndex />} />
        <Route path="/legal/terms" element={<LazyTermsPage />} />
        <Route path="/legal/privacy" element={<LazyPrivacyPage />} />
        <Route path="/legal/acceptable-use" element={<LazyAcceptableUsePage />} />
        <Route path="/legal/dpa" element={<LazyDpaPage />} />
        <Route path="/client/:token" element={<ClientApproval />} />
        <Route path="/decisions/client/:token" element={<DecisionClientPortal />} />
        <Route path="/vendor-quote/:token" element={<VendorQuotePage />} />
        <Route path="/dashboard" element={<ProtectedRoute><StudioDashboard /></ProtectedRoute>} />
        <Route path="/crm" element={<ProtectedRoute><CRM /></ProtectedRoute>} />
        <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
        <Route path="/proposals/:proposalId/edit" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
        <Route path="/proposals/:proposalId/preview" element={<ProtectedRoute><ProposalPreview /></ProtectedRoute>} />
        <Route path="/proposals/:proposalId/compare" element={<ProtectedRoute><ProposalCompare /></ProtectedRoute>} />
        <Route path="/proposals/client/:token" element={<ProposalClientView />} />
        <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
        <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
        <Route path="/vendors/:vendorId" element={<ProtectedRoute><VendorProfile /></ProtectedRoute>} />
        <Route path="/studio" element={<ProtectedRoute><StudioIdentity /></ProtectedRoute>} />
        <Route path="/studio-identity" element={<Navigate to="/studio" />} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        <Route path="/support/:ticketId" element={<ProtectedRoute><SupportTicketDetail /></ProtectedRoute>} />
        <Route path="/superadmin" element={<ProtectedRoute><SuperadminLayout /></ProtectedRoute>}>
          <Route index element={<SAOverview />} />
          <Route path="studios" element={<SAStudios />} />
          <Route path="studios/:studioId" element={<SAStudioDetail />} />
          <Route path="users" element={<SAUsers />} />
          <Route path="billing" element={<SABilling />} />
          <Route path="activity" element={<SAActivity />} />
          <Route path="usage" element={<SAUsage />} />
          <Route path="errors" element={<SAErrors />} />
          <Route path="system" element={<SASystem />} />
          <Route path="tests" element={<SATests />} />
                    <Route path="test-dashboard" element={<SATestDashboard />} />
                    <Route path="e2e-tests" element={<SAE2ETests />} />
          <Route path="utilities" element={<SAUtilities />} />
          <Route path="support" element={<SASupport />} />
          <Route path="support/:ticketId" element={<SASupportDetail />} />
        </Route>
        {/* Vendor Portal */}
        <Route path="/vendor/login" element={<VendorLogin />} />
        <Route path="/vendor/set-password" element={<VendorSetPassword />} />
        <Route path="/vendor" element={<VendorLayout />}>
          <Route path="dashboard" element={<VendorDashboard />} />
          <Route path="rfqs" element={<VendorRFQs />} />
          <Route path="rfqs/:rfqId" element={<VendorRFQDetail />} />
          <Route path="purchase-orders" element={<VendorPOs />} />
          <Route path="payments" element={<VendorPayments />} />
          <Route path="profile" element={<VendorProfilePage />} />
        </Route>
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <VendorAuthProvider>
            <AppRouter />
          </VendorAuthProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
